// DRI-253: one group per system user on the Facebook Connections page: its Business Manager and the businesses
// whose assets it connects.
import React, { useEffect, useMemo, useState } from 'react'
import {
  Chip,
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import { toast } from 'react-toastify'
import {
  BusinessError,
  SystemUserAssetConnection,
  SystemUserAssetType,
  SystemUserBusinessConnection,
} from '@marketing-milk/interfaces'

import { businessMatches, normalizeSearch, textMatches } from './search'
import {
  BUSINESS_ERROR_HINTS,
  BUSINESS_ERROR_LABELS,
  errorSeverity,
  hasCritical,
  hasWarning,
  isFiltering,
  IssueFilter,
  matchesIssueFilter,
  severityStyle,
} from './errorLabels'

const CONNECTIONS_PER_PAGE = 25

const ASSET_TYPE_LABELS: Record<SystemUserAssetType, string> = {
  adaccount: 'Ad Account',
  page: 'Page',
  dataset: 'Dataset',
  offline: 'Offline Dataset',
  instagram: 'Instagram',
}

async function copyToClipboard(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value)
    toast.success(`${label} copied to clipboard!`)
  } catch {
    toast.error(`Failed to copy ${label.toLowerCase()}.`)
  }
}

// small inline copy affordance for an ID — used for system user IDs, BM IDs, and per-asset FB IDs so
// staff can grab the exact value to search Facebook's Business Manager / Graph API Explorer directly.
export function CopyableId({ value, label }: { value: string; label: string }) {
  return (
    <Tooltip title={`Copy ${label}`}>
      <IconButton
        size="small"
        style={{ padding: 2 }}
        onClick={e => {
          e.stopPropagation()
          copyToClipboard(value, label)
        }}
      >
        <FileCopyOutlinedIcon style={{ fontSize: 13 }} />
      </IconButton>
    </Tooltip>
  )
}

export function matchesSystemUserSearch(
  connection: SystemUserBusinessConnection,
  query: string
): boolean {
  const q = normalizeSearch(query)
  if (!q) return true
  if (textMatches(connection.system_user_id, q)) return true
  if (textMatches(connection.client_business_manager_id, q)) return true
  if (textMatches(connection.fb_business_name, q)) return true
  return connection.connections.some(c => businessMatches(c, [c.asset_id], q))
}

// one line per business (mirrors PersonRows' toPersonBusinessRows) — a business can have several
// assets on the same system user (ad account + page, say), each with its own monitored/error state,
// so they're combined onto a single row rather than one row per asset.
interface BusinessRow {
  business_id: number
  business_name: string
  assets: SystemUserAssetConnection[]
}

function toBusinessRows(connection: SystemUserBusinessConnection): BusinessRow[] {
  const map = new Map<number, BusinessRow>()
  for (const asset of connection.connections) {
    const existing = map.get(asset.business_id)
    if (existing) {
      existing.assets.push(asset)
    } else {
      map.set(asset.business_id, {
        business_id: asset.business_id,
        business_name: asset.business_name,
        assets: [asset],
      })
    }
  }
  return Array.from(map.values())
}

export function SystemUserRow({
  connection,
  searchQuery,
  issueFilter,
}: {
  connection: SystemUserBusinessConnection
  searchQuery: string
  issueFilter: IssueFilter
}) {
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)

  useEffect(() => {
    setPage(1)
  }, [searchQuery, issueFilter])

  const allRows = useMemo(() => toBusinessRows(connection), [connection])

  const filteredRows = useMemo(() => {
    const q = normalizeSearch(searchQuery)
    let rows = allRows
    if (isFiltering(issueFilter)) {
      rows = rows.filter(r => r.assets.some(a => matchesIssueFilter(a.errors, issueFilter)))
    }
    if (!q) return rows
    // a query matching the system user id, BM id, or resolved BM name qualifies the whole group —
    // don't additionally filter its business rows by name in that case, or every business with a
    // non-matching name would vanish even though the group itself is what the search was looking for.
    const matchesGroupIdentity =
      connection.system_user_id.toLowerCase().includes(q) ||
      connection.client_business_manager_id.toLowerCase().includes(q) ||
      (connection.fb_business_name?.toLowerCase().includes(q) ?? false)
    if (matchesGroupIdentity) return rows
    return rows.filter(r =>
      businessMatches(
        r,
        r.assets.map(a => a.asset_id),
        q
      )
    )
  }, [
    allRows,
    connection.system_user_id,
    connection.client_business_manager_id,
    connection.fb_business_name,
    searchQuery,
    issueFilter,
  ])

  const totalPages = Math.ceil(filteredRows.length / CONNECTIONS_PER_PAGE)
  const pageRows = filteredRows.slice(
    (page - 1) * CONNECTIONS_PER_PAGE,
    page * CONNECTIONS_PER_PAGE
  )
  const errorCount = filteredRows.filter(r => r.assets.some(a => hasCritical(a.errors))).length
  const warningCount = filteredRows.filter(r => r.assets.some(a => hasWarning(a.errors))).length
  const businessCount = allRows.length

  useEffect(() => {
    if (searchQuery.trim() && filteredRows.length > 0) setOpen(true)
  }, [searchQuery, filteredRows.length])

  if (filteredRows.length === 0 && (searchQuery.trim() || isFiltering(issueFilter))) return null

  return (
    <div
      style={{ marginBottom: 8, border: '1px solid #e0e0e0', borderRadius: 4, overflow: 'hidden' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '10px 16px',
          cursor: 'pointer',
          background: open ? '#fafafa' : '#fff',
          userSelect: 'none',
        }}
        onClick={() => setOpen(v => !v)}
      >
        <IconButton
          size="small"
          style={{ padding: 2 }}
          onClick={e => {
            e.stopPropagation()
            setOpen(v => !v)
          }}
        >
          {open ? (
            <KeyboardArrowDownIcon fontSize="small" />
          ) : (
            <KeyboardArrowRightIcon fontSize="small" />
          )}
        </IconButton>
        {connection.fb_business_picture_uri ? (
          <img
            src={connection.fb_business_picture_uri}
            alt={connection.fb_business_name ?? connection.client_business_manager_id}
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              flexShrink: 0,
              objectFit: 'cover',
            }}
          />
        ) : (
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              background: '#F3E8FF',
              color: '#7C3AED',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: 13,
              flexShrink: 0,
            }}
          >
            {connection.fb_business_name?.[0]?.toUpperCase() ?? 'S'}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="subtitle2" style={{ fontWeight: 600, lineHeight: 1.2 }}>
              {connection.fb_business_name ?? 'Unknown Business'} (
              {connection.client_business_manager_id})
            </Typography>
            <CopyableId value={connection.client_business_manager_id} label="Business Manager ID" />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" color="textSecondary" style={{ fontFamily: 'monospace' }}>
              System User {connection.system_user_id}
            </Typography>
            <CopyableId value={connection.system_user_id} label="System User ID" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Chip
            label={`${businessCount} business${businessCount !== 1 ? 'es' : ''}`}
            size="small"
          />
          {errorCount > 0 && (
            <Chip
              icon={<WarningIcon style={{ fontSize: 13 }} />}
              label={`${errorCount} error${errorCount !== 1 ? 's' : ''}`}
              size="small"
              style={{
                background: severityStyle.critical.chipBackground,
                color: severityStyle.critical.chipText,
              }}
            />
          )}
          {warningCount > 0 && (
            <Chip
              icon={<WarningIcon style={{ fontSize: 13 }} />}
              label={`${warningCount} warning${warningCount !== 1 ? 's' : ''}`}
              size="small"
              style={{
                background: severityStyle.warning.chipBackground,
                color: severityStyle.warning.chipText,
              }}
            />
          )}
        </div>
      </div>

      <Collapse in={open} unmountOnExit>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow style={{ background: '#f5f5f5' }}>
                <TableCell>Business</TableCell>
                <TableCell>Assets</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map(row => (
                <TableRow key={row.business_id}>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Link
                        href={`/admin/business/beta/accounts/edit/${row.business_id}`}
                        variant="body2"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        {row.business_name}
                      </Link>
                      <CopyableId value={String(row.business_id)} label="Business ID" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {row.assets.map(asset => (
                        <div
                          key={asset.asset_type}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Chip
                            label={ASSET_TYPE_LABELS[asset.asset_type]}
                            size="small"
                            style={{
                              height: 18,
                              fontSize: 11,
                              background: '#EEF4FF',
                              color: '#1877F2',
                              fontWeight: 600,
                            }}
                          />
                          {asset.asset_id && (
                            <>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                                style={{ fontFamily: 'monospace' }}
                              >
                                {asset.asset_id}
                              </Typography>
                              <CopyableId
                                value={asset.asset_id}
                                label={`${ASSET_TYPE_LABELS[asset.asset_type]} ID`}
                              />
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <BusinessAssetsStatus assets={row.assets} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 8,
              padding: '6px 12px',
              borderTop: '1px solid #e0e0e0',
            }}
            onClick={e => e.stopPropagation()}
          >
            <Typography variant="caption" color="textSecondary">
              {(page - 1) * CONNECTIONS_PER_PAGE + 1}–
              {Math.min(page * CONNECTIONS_PER_PAGE, filteredRows.length)} of {filteredRows.length}
            </Typography>
            <Tooltip title="Previous">
              <span>
                <IconButton size="small" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>
                  <NavigateBeforeIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next">
              <span>
                <IconButton
                  size="small"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page >= totalPages}
                >
                  <NavigateNextIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        )}
      </Collapse>
    </div>
  )
}

// combines every asset this business has on this system user into one status cell — a business can
// have multiple broken assets at once (e.g. Page AND Ad Account each with their own errors), so each
// asset's problems are listed with which asset they belong to, rather than only showing one status.
function BusinessAssetsStatus({ assets }: { assets: SystemUserAssetConnection[] }) {
  const monitored = assets.filter(a => a.monitored)
  const unmonitored = assets.filter(a => !a.monitored)
  const broken = monitored.filter(a => (a.errors?.length ?? 0) > 0)

  if (broken.length === 0 && unmonitored.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <CheckCircleIcon style={{ fontSize: 15, color: '#4caf50' }} />
        <Typography variant="caption" style={{ color: '#757575' }}>
          Connected
        </Typography>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {broken.map(asset =>
        (asset.errors ?? []).map(err => (
          <FlagLine
            key={`${asset.asset_type}-${err}`}
            error={err}
            label={`${ASSET_TYPE_LABELS[asset.asset_type]}: ${BUSINESS_ERROR_LABELS[err]}`}
          />
        ))
      )}
      {monitored.length > broken.length && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <CheckCircleIcon style={{ fontSize: 15, color: '#4caf50' }} />
          <Typography variant="caption" style={{ color: '#757575' }}>
            Other assets connected
          </Typography>
        </div>
      )}
      {unmonitored.length > 0 && (
        <Tooltip title="No access-error detection exists for this asset type yet">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <HelpOutlineIcon style={{ fontSize: 15, color: '#9e9e9e' }} />
            <Typography variant="caption" style={{ color: '#9e9e9e' }}>
              {unmonitored.map(a => ASSET_TYPE_LABELS[a.asset_type]).join(', ')} not monitored
            </Typography>
          </div>
        </Tooltip>
      )}
    </div>
  )
}

// one flag on a status cell, coloured by severity (DRI-462), with its hint on hover where it has one
export function FlagLine({ error, label }: { error: BusinessError; label: string }) {
  const color = severityStyle[errorSeverity(error)].icon
  const line = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <WarningIcon style={{ fontSize: 15, color }} />
      <Typography variant="caption" style={{ color }}>
        {label}
      </Typography>
    </div>
  )
  const hint = BUSINESS_ERROR_HINTS[error]
  return hint ? <Tooltip title={hint}>{line}</Tooltip> : line
}
